import { useEffect, useRef, useCallback } from "react";
import ReactDOM from "react-dom";

import { Overlay, Panel, ScrollRegion } from "./styles";

// Accessible Modal Implementation (replaces Headless UI Dialog)
// Features:
// - Focus trap within modal
// - Return focus to triggering element
// - ESC key to close
// - ARIA attributes for role dialog and labelledby
// - Prevent background scroll while open
// - Click overlay to close

function useFocusTrap(isOpen, panelRef) {
  useEffect(() => {
    if (!isOpen || !panelRef.current) return;
    const panel = panelRef.current;
    const focusableSelectors = [
      "a[href]",
      "area[href]",
      'input:not([disabled]):not([type="hidden"])',
      "select:not([disabled])",
      "textarea:not([disabled])",
      "button:not([disabled])",
      "iframe",
      "object",
      "embed",
      '*[tabindex]:not([tabindex="-1"])',
      '*[contenteditable="true"]',
    ];
    const getFocusable = () =>
      Array.from(panel.querySelectorAll(focusableSelectors.join(",")));
    const previouslyFocused = document.activeElement;
    // Focus first focusable or panel itself
    const first = getFocusable()[0];
    (first || panel).focus();

    function handleKey(e) {
      if (e.key === "Tab") {
        const focusable = getFocusable();
        if (focusable.length === 0) {
          e.preventDefault();
          panel.focus();
          return;
        }
        const idx = focusable.indexOf(document.activeElement);
        if (e.shiftKey) {
          if (idx <= 0) {
            e.preventDefault();
            focusable[focusable.length - 1].focus();
          }
        } else {
          if (idx === focusable.length - 1) {
            e.preventDefault();
            focusable[0].focus();
          }
        }
      }
    }
    panel.addEventListener("keydown", handleKey);
    return () => {
      panel.removeEventListener("keydown", handleKey);
      if (previouslyFocused && previouslyFocused.focus)
        previouslyFocused.focus();
    };
  }, [isOpen, panelRef]);
}

function usePreventScroll(isOpen) {
  useEffect(() => {
    if (isOpen) {
      const original = document.body.style.overflow;
      document.body.style.overflow = "hidden";
      return () => {
        document.body.style.overflow = original;
      };
    }
  }, [isOpen]);
}

export default function MyModal({
  children,
  isOpen,
  closeModal,
  labelledBy = "modal-title",
  describedBy = "modal-description",
}) {
  const panelRef = useRef(null);
  const overlayRef = useRef(null);
  useFocusTrap(isOpen, panelRef);
  usePreventScroll(isOpen);

  const handleKeyDown = useCallback(
    (e) => {
      if (e.key === "Escape") {
        e.stopPropagation();
        closeModal();
      }
    },
    [closeModal]
  );

  useEffect(() => {
    if (!isOpen) return;
    document.addEventListener("keydown", handleKeyDown, true);
    return () => document.removeEventListener("keydown", handleKeyDown, true);
  }, [isOpen, handleKeyDown]);

  const handleOverlayClick = (e) => {
    if (e.target === overlayRef.current) {
      closeModal();
    }
  };

  if (!isOpen) return null;

  const modalContent = (
    <div
      aria-modal="true"
      role="dialog"
      aria-labelledby={labelledBy}
      aria-describedby={describedBy}
    >
      <Overlay ref={overlayRef} onClick={handleOverlayClick} />
      <ScrollRegion>
        <Panel ref={panelRef} tabIndex="-1">
          {children}
        </Panel>
      </ScrollRegion>
    </div>
  );

  return ReactDOM.createPortal(modalContent, document.body);
}
